import {
  ArrowLeft,
  CalendarDays,
  Globe2,
  LogOut,
  Mail,
  Save,
  Settings,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { safeReturnTo, signInPath } from "../authReturn";
import { LoadingButton } from "../components/LoadingButton";
import { TurnstileWidget, type TurnstileWidgetHandle } from "../components/TurnstileWidget";
import { useAppStore } from "../state";
import { supabase } from "../supabase";
import { getUserDisplayName, useAuthSession } from "../useAuthSession";

export function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get("returnTo"));
  const { session, loading } = useAuthSession();
  const guest = useAppStore((state) => state.guest);
  const setGuest = useAppStore((state) => state.setGuest);
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [ageSetupNeeded, setAgeSetupNeeded] = useState(false);
  const [guardianReview, setGuardianReview] = useState<{
    pending: boolean;
    emailHint: string | null;
  }>({ pending: false, emailHint: null });
  const [resendingConsent, setResendingConsent] = useState(false);
  const [message, setMessage] = useState("");
  const [countries, setCountries] = useState<Array<{ code: string; name: string; flag: string }>>(
    [],
  );
  const [countryCode, setCountryCode] = useState("");
  const [showCountryFlag, setShowCountryFlag] = useState(true);
  const [creatingGuest, setCreatingGuest] = useState(false);
  const [guestChallengeToken, setGuestChallengeToken] = useState<string | null>(null);
  const guestChallengeRef = useRef<TurnstileWidgetHandle>(null);
  const receiveGuestChallenge = useCallback(
    (token: string | null) => setGuestChallengeToken(token),
    [],
  );

  useEffect(() => {
    if (session) setDisplayName(getUserDisplayName(session.user));
    else if (guest) setDisplayName(guest.displayName);
  }, [guest, session]);

  useEffect(() => {
    if (!session) return;
    let active = true;
    void api
      .onboardingStatus()
      .then((status) => {
        if (active) {
          setAgeSetupNeeded(!status.dateOfBirthSet);
          setGuardianReview({
            pending: status.parentalConsentStatus === "pending",
            emailHint: status.guardianEmailHint,
          });
          if (
            status.parentalConsentStatus === "verified" &&
            session.user.app_metadata.parental_consent_status !== "verified"
          )
            void supabase?.auth.refreshSession();
        }
      })
      .catch(() => {
        if (active)
          setAgeSetupNeeded(
            session.user.app_metadata.onboarding_completed !== true ||
              !["child", "teen", "adult"].includes(
                String(session.user.app_metadata.age_band ?? ""),
              ),
          );
      });
    return () => {
      active = false;
    };
  }, [session]);

  useEffect(() => {
    if (!session && !guest) return;
    let active = true;
    void Promise.all([api.countries(), api.localeSettings()])
      .then(([options, locale]) => {
        if (!active) return;
        setCountries(options);
        setCountryCode(locale.context.country_code ?? "");
        setShowCountryFlag(locale.showCountryFlag);
      })
      .catch((error) => {
        if (active)
          setMessage(error instanceof Error ? error.message : "Country settings could not load.");
      });
    return () => {
      active = false;
    };
  }, [guest, session]);

  useEffect(() => {
    if (session || guest) return;
    let active = true;
    void Promise.all([api.countries(), api.visitorContext()])
      .then(([options, visitor]) => {
        if (!active) return;
        setCountries(options);
        setCountryCode(visitor.countryCode ?? "");
      })
      .catch((error) => {
        if (active)
          setMessage(error instanceof Error ? error.message : "Guest settings could not load.");
      });
    return () => {
      active = false;
    };
  }, [guest, session]);

  const createGuest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!guestChallengeToken || !displayName.trim()) return;
    setCreatingGuest(true);
    setMessage("");
    try {
      const created = await api.createGuest(guestChallengeToken);
      const configuredGuest = { ...created, displayName: displayName.trim() };
      setGuest(configuredGuest);
      if (countryCode) {
        try {
          await api.updateLocaleSettings({ countryCode, showCountryFlag });
        } catch {
          // Guest play should still open if optional locale persistence is temporarily unavailable.
        }
      }
      navigate(returnTo, { replace: true });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Guest play could not be started.");
      setGuestChallengeToken(null);
      guestChallengeRef.current?.reset();
    } finally {
      setCreatingGuest(false);
    }
  };

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!session && !guest) return;
    setSaving(true);
    setMessage("");
    try {
      if (session) {
        if (!supabase) throw new Error("Account settings are unavailable.");
        const { error } = await supabase.auth.updateUser({
          data: { display_name: displayName.trim() },
        });
        if (error) throw error;
      } else if (guest) {
        setGuest({ ...guest, displayName: displayName.trim() });
      }
      if (!countryCode) throw new Error("Choose your country.");
      await api.updateLocaleSettings({ countryCode, showCountryFlag });
      setMessage("Your profile and country visibility have been updated.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Settings could not be saved.");
    }
    setSaving(false);
  };

  const signOut = async () => {
    if (!supabase) return;
    setSigningOut(true);
    await supabase.auth.signOut();
    navigate("/");
  };

  const resendGuardianReview = async () => {
    setResendingConsent(true);
    setMessage("");
    try {
      const result = await api.resendParentalConsent();
      setMessage(`A fresh guardian review link was sent to ${result.guardianEmailHint}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The guardian email could not be sent.");
    } finally {
      setResendingConsent(false);
    }
  };

  if (loading)
    return (
      <section className="empty-state">
        <span className="account-skeleton" />
        <p>Checking your account…</p>
      </section>
    );

  if (!session && !guest)
    return (
      <section className="settings-page guest-setup-page">
        <Link className="text-link" to="/">
          <ArrowLeft /> Back to home
        </Link>
        <div className="settings-card">
          <span className="icon-tile">
            <UserRound />
          </span>
          <span className="eyebrow">PLAY ANONYMOUSLY</span>
          <h1>Set up your guest profile.</h1>
          <p>Choose how you appear in rooms. No camera or microphone permission is requested.</p>
          <form onSubmit={(event) => void createGuest(event)}>
            <label>
              What should players call you?
              <input
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value.slice(0, 40))}
                placeholder="Your nickname"
                minLength={1}
                maxLength={40}
                required
                disabled={creatingGuest}
              />
            </label>
            <label>
              Country
              <select
                value={countryCode}
                onChange={(event) => setCountryCode(event.target.value)}
                disabled={creatingGuest || countries.length === 0}
              >
                <option value="">Choose country (optional)</option>
                {countries.map((country) => (
                  <option value={country.code} key={country.code}>
                    {country.flag} {country.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="settings-checkbox">
              <input
                type="checkbox"
                checked={showCountryFlag}
                onChange={(event) => setShowCountryFlag(event.target.checked)}
                disabled={creatingGuest}
              />
              <span>
                Show my country flag in game rooms
                <small>Your city, region and IP address are never shown to other players.</small>
              </span>
            </label>
            <TurnstileWidget
              ref={guestChallengeRef}
              action="guest_join"
              onToken={receiveGuestChallenge}
            />
            <LoadingButton
              type="submit"
              className="button button-primary wide"
              loading={creatingGuest}
              loadingLabel="Starting guest play…"
              disabled={!displayName.trim() || !guestChallengeToken}
            >
              <UserRound /> Save and continue
            </LoadingButton>
            <Link className="button button-ghost wide" to={signInPath(returnTo)}>
              Sign in instead
            </Link>
          </form>
          {message && (
            <div className="status-message" role="status">
              {message}
            </div>
          )}
        </div>
      </section>
    );

  return (
    <section className="settings-page">
      <Link className="text-link" to="/app">
        <ArrowLeft /> Back to dashboard
      </Link>
      <div className="settings-card">
        <span className="icon-tile">
          <Settings />
        </span>
        <span className="eyebrow">ACCOUNT SETTINGS</span>
        <h1>{session ? "Your Topzle profile" : "Your guest profile"}</h1>
        <p>Control how you appear in rooms and manage the current session.</p>
        {session && ageSetupNeeded && (
          <div className="settings-age-setup">
            <CalendarDays />
            <div>
              <strong>Finish your one-time age setup</strong>
              <small>
                Add your date of birth once so Topzle can apply the correct room and call safety.
              </small>
            </div>
            <Link
              className="button button-primary compact"
              to="/onboarding"
              state={{ returnTo: "/settings" }}
            >
              Add date of birth
            </Link>
          </div>
        )}
        {session && guardianReview.pending && (
          <div className="settings-age-setup guardian-review-pending">
            <ShieldCheck />
            <div>
              <strong>Guardian approval is pending</strong>
              <small>
                Social play stays locked until the link sent to{" "}
                {guardianReview.emailHint ?? "your parent or guardian"} is approved.
              </small>
            </div>
            <LoadingButton
              type="button"
              className="button button-primary compact"
              onClick={() => void resendGuardianReview()}
              loading={resendingConsent}
              loadingLabel="Sending…"
            >
              <Mail /> Resend
            </LoadingButton>
          </div>
        )}
        <form onSubmit={(event) => void save(event)}>
          <label>
            Display name
            <input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value.slice(0, 40))}
              minLength={1}
              maxLength={40}
              required
              disabled={saving}
            />
          </label>
          {session && (
            <label>
              Email
              <input
                value={
                  session.user.email?.endsWith("@auth.topzle.com") ? "" : (session.user.email ?? "")
                }
                placeholder="Add your email during profile setup"
                disabled
                readOnly
              />
            </label>
          )}
          <div className="settings-locale-heading">
            <Globe2 />
            <div>
              <strong>Country and local defaults</strong>
              <small>Used for phone prefixes, dates, currency and your optional room flag.</small>
            </div>
          </div>
          <label>
            Country
            <select
              value={countryCode}
              onChange={(event) => setCountryCode(event.target.value)}
              disabled={saving || countries.length === 0}
              required
            >
              <option value="">Choose country</option>
              {countries.map((country) => (
                <option value={country.code} key={country.code}>
                  {country.flag} {country.name}
                </option>
              ))}
            </select>
          </label>
          <label className="settings-checkbox">
            <input
              type="checkbox"
              checked={showCountryFlag}
              onChange={(event) => setShowCountryFlag(event.target.checked)}
              disabled={saving}
            />
            <span>
              Show my country flag in game rooms
              <small>Your city, region and IP address are never shown to other players.</small>
            </span>
          </label>
          <LoadingButton
            type="submit"
            className="button button-primary wide"
            loading={saving}
            loadingLabel="Saving…"
            disabled={!displayName.trim() || !countryCode || signingOut}
          >
            <Save /> Save profile
          </LoadingButton>
        </form>
        {message && (
          <div className="status-message" role="status">
            {message}
          </div>
        )}
        {session && (
          <div className="settings-danger">
            <div>
              <strong>Sign out on this device</strong>
              <small>You can sign back in any time with a phone verification code.</small>
            </div>
            <LoadingButton
              type="button"
              className="button button-ghost"
              onClick={() => void signOut()}
              loading={signingOut}
              loadingLabel="Signing out…"
              disabled={saving}
            >
              <LogOut /> Sign out
            </LoadingButton>
          </div>
        )}
      </div>
    </section>
  );
}
